import { useState, useCallback, useRef, useEffect } from 'react';
import type { ColumnWidths } from './useUserPreferences';

interface UseColumnResizeOptions {
  defaultWidths: ColumnWidths;
  savedWidths?: ColumnWidths | null;
  onSave: (widths: ColumnWidths) => void;
  minWidth?: number;
  maxWidth?: number;
}

interface UseColumnResizeReturn {
  columnWidths: ColumnWidths;
  isResizing: boolean;
  resizingColumn: string | null;
  handleResizeStart: (columnKey: string, startX: number) => void;
  getColumnStyle: (columnKey: string) => React.CSSProperties;
  resetWidths: () => void;
}

export function useColumnResize({
  defaultWidths,
  savedWidths,
  onSave,
  minWidth = 80,
  maxWidth = 600,
}: UseColumnResizeOptions): UseColumnResizeReturn {
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(() => ({
    ...defaultWidths,
    ...(savedWidths || {}),
  }));

  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);

  const resizeState = useRef({
    columnKey: '',
    startX: 0,
    startWidth: 0,
  });

  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const prevSavedWidthsRef = useRef<string>('');

  useEffect(() => {
    if (savedWidths) {
      const newWidthsStr = JSON.stringify(savedWidths);
      if (newWidthsStr !== prevSavedWidthsRef.current) {
        prevSavedWidthsRef.current = newWidthsStr;
        setColumnWidths({ ...defaultWidths, ...savedWidths });
      }
    }
  }, [savedWidths, defaultWidths]);

  const debouncedSave = useCallback((widths: ColumnWidths) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      onSave(widths);
    }, 500);
  }, [onSave]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !resizeState.current.columnKey) return;

    const deltaX = e.clientX - resizeState.current.startX;
    const newWidth = Math.max(
      minWidth,
      Math.min(maxWidth, resizeState.current.startWidth + deltaX)
    );

    setColumnWidths((prev) => {
      const updated = {
        ...prev,
        [resizeState.current.columnKey]: newWidth,
      };
      return updated;
    });
  }, [isResizing, minWidth, maxWidth]);

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      setResizingColumn(null);
      debouncedSave(columnWidths);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }, [isResizing, columnWidths, debouncedSave]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleResizeStart = useCallback((columnKey: string, startX: number) => {
    resizeState.current = {
      columnKey,
      startX,
      startWidth: columnWidths[columnKey] || defaultWidths[columnKey],
    };
    setIsResizing(true);
    setResizingColumn(columnKey);
  }, [columnWidths, defaultWidths]);

  const getColumnStyle = useCallback((columnKey: string): React.CSSProperties => {
    return {
      width: `${columnWidths[columnKey] || defaultWidths[columnKey]}px`,
      minWidth: `${columnWidths[columnKey] || defaultWidths[columnKey]}px`,
      maxWidth: `${columnWidths[columnKey] || defaultWidths[columnKey]}px`,
    };
  }, [columnWidths, defaultWidths]);

  const resetWidths = useCallback(() => {
    setColumnWidths(defaultWidths);
    onSave(defaultWidths);
  }, [defaultWidths, onSave]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    columnWidths,
    isResizing,
    resizingColumn,
    handleResizeStart,
    getColumnStyle,
    resetWidths,
  };
}
